import * as Sentry from '@sentry/vue'

const isProduction = () => process.env.NODE_ENV === 'production'

/**
 * プリンタ関連エラーを Sentry に送信する。
 * production 環境以外では何もしない（既存の console.error / トーストはそのまま）。
 *
 * 個人情報（顧客名・電話番号・注文内容など）は絶対に context へ含めないこと。
 *
 * @param {unknown} error - 例外オブジェクト
 * @param {object} context
 * @param {'receipt'|'kitchen'} context.kind - プリンタ種別
 * @param {'connect'|'print'|'print-response'|'connect-test'} context.phase - 発生フェーズ
 * @param {string} [context.ipAddress] - 接続先 IP
 * @param {number|string} [context.clubId] - 店舗 ID（currentClub.value?.id）
 * @param {string} [context.printerName] - キッチンプリンタ名など
 * @param {'qr-code'|'estimated'|'receipt-slip'|'settlement'|'debt-confirmation'|'accounting'} [context.slipKind] - レシートプリンタの slip 種別
 * @param {string|number} [context.responseCode] - onReceive 経由のときの ePOS 応答コード
 */
export function reportPrinterError(error, context = {}) {
  if (!isProduction()) return

  const {
    kind, phase, ipAddress, clubId, printerName, slipKind, responseCode,
  } = context

  Sentry.captureException(error, {
    tags: {
      feature: 'printer',
      kind,
      phase,
      ...(slipKind ? { slipKind } : {}),
    },
    extra: {
      ipAddress,
      clubId,
      printerName,
      responseCode,
    },
  })
}

/**
 * ePOS SDK の onReceive ハンドラをラップし、`response.success === false` の場合に
 * Sentry に通知してから元の handler を実行する。
 *
 * @template {(response: { success: boolean, code?: string|number }) => void} H
 * @param {H} handler - 元の onReceive ハンドラ
 * @param {object} context - reportPrinterError と同じ shape（phase は自動で 'print-response'）
 * @returns {H}
 */
export function wrapPrinterReceive(handler, context = {}) {
  return (response => {
    if (!response?.success) {
      reportPrinterError(
        new Error(`Printer response error: ${response?.code ?? 'unknown'}`),
        { ...context, phase: 'print-response', responseCode: response?.code },
      )
    }
    if (typeof handler === 'function') handler(response)
  })
}
